import { useCallback, useEffect, useState } from 'react';
import { ListMusic } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import ContinueListeningMenu from './ContinueListeningMenu';
import MobileRadioFAB from './MobileRadioFAB';
import NowPlayingDiscovery from './NowPlayingDiscovery';
import { useAudio } from '../contexts/AudioContext';

const MOBILE_NAV_HEIGHT = 64;
const MEDIA_ACTION_BAR_HEIGHT = 64;
const PLAYER_SELECTORS = [
  '[data-testid="audio-player"]',
  '[data-testid="persistent-video-mini-player"]',
];

const getVisibleElementHeight = (element) => {
  if (!element || element.hidden) return 0;
  const styles = window.getComputedStyle(element);
  if (styles.display === 'none' || styles.visibility === 'hidden') return 0;
  return Math.max(0, Math.ceil(element.getBoundingClientRect().height || 0));
};

const getVisiblePlayerHeight = (root = document) => Math.max(
  0,
  ...PLAYER_SELECTORS.map((selector) => getVisibleElementHeight(root.querySelector(selector))),
);

const MobileMediaActionBar = () => {
  const location = useLocation();
  const { playerPresentation } = useAudio();
  const minimized = playerPresentation === 'minimized';
  const [playerHeight, setPlayerHeight] = useState(0);

  const measurePlayers = useCallback(() => {
    const nextHeight = getVisiblePlayerHeight();
    setPlayerHeight((currentHeight) => (currentHeight === nextHeight ? currentHeight : nextHeight));
  }, []);

  useEffect(() => {
    let resizeObserver;
    let rootObserver;
    let playerObservers = [];
    let observedPlayers = [];

    const observePlayers = () => {
      const players = PLAYER_SELECTORS
        .map((selector) => document.querySelector(selector))
        .filter(Boolean);
      if (players.length === observedPlayers.length && players.every((player, index) => player === observedPlayers[index])) {
        measurePlayers();
        return;
      }

      resizeObserver?.disconnect();
      playerObservers.forEach((observer) => observer.disconnect());
      playerObservers = [];
      observedPlayers = players;

      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(measurePlayers);
        players.forEach((player) => resizeObserver.observe(player));
      }

      if (typeof MutationObserver !== 'undefined') {
        playerObservers = players.map((player) => {
          const observer = new MutationObserver(measurePlayers);
          observer.observe(player, { attributes: true, attributeFilter: ['class', 'hidden', 'style'] });
          return observer;
        });
      }
      measurePlayers();
    };

    observePlayers();
    window.addEventListener('resize', measurePlayers);
    if (typeof MutationObserver !== 'undefined' && document.body) {
      rootObserver = new MutationObserver(observePlayers);
      rootObserver.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      window.removeEventListener('resize', measurePlayers);
      resizeObserver?.disconnect();
      rootObserver?.disconnect();
      playerObservers.forEach((observer) => observer.disconnect());
    };
  }, [measurePlayers]);

  useEffect(() => {
    const occupiedHeight = MOBILE_NAV_HEIGHT + (minimized ? 0 : MEDIA_ACTION_BAR_HEIGHT) + playerHeight;
    document.documentElement.style.setProperty(
      '--rastyle-mobile-media-stack-height',
      `calc(${occupiedHeight}px + env(safe-area-inset-bottom, 0px))`,
    );
    return () => document.documentElement.style.removeProperty('--rastyle-mobile-media-stack-height');
  }, [minimized, playerHeight]);

  const bottom = `calc(${MOBILE_NAV_HEIGHT + playerHeight}px + env(safe-area-inset-bottom, 0px))`;
  const playlistsActive = location.pathname.startsWith('/playlists');

  if (minimized) return null;

  return (
    <nav
      aria-label="Listening shortcuts"
      className="fixed inset-x-0 z-[60] h-16 border-y border-white/10 bg-black/95 shadow-[0_-12px_30px_rgba(0,0,0,0.55)] backdrop-blur-xl md:hidden"
      data-testid="mobile-media-action-bar"
      style={{ bottom }}
    >
      <div className="grid h-full grid-cols-4">
        <ContinueListeningMenu variant="action-bar" />
        <Link
          to="/playlists"
          aria-current={playlistsActive ? 'page' : undefined}
          className={`flex min-w-0 flex-col items-center justify-center gap-1 px-1 text-[10px] font-bold uppercase tracking-wide transition ${playlistsActive ? 'bg-rastyle-orange/10 text-rastyle-orange' : 'text-zinc-300 hover:bg-white/5 hover:text-white'}`}
          data-testid="mobile-action-playlists"
        >
          <ListMusic className="h-5 w-5" />
          <span>Playlists</span>
        </Link>
        <NowPlayingDiscovery variant="action-bar" />
        <MobileRadioFAB variant="action-bar" />
      </div>
    </nav>
  );
};

export {
  MEDIA_ACTION_BAR_HEIGHT,
  MOBILE_NAV_HEIGHT,
  getVisibleElementHeight,
  getVisiblePlayerHeight,
};
export default MobileMediaActionBar;
